/**
 * Permission-related React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { identityServiceClient } from '../client'
import { PermissionsQueryParams, PermissionTestRequest } from '../'

// Query hooks for permissions
export const usePermissions = (params?: PermissionsQueryParams) => {
  return useQuery({
    queryKey: ['permissions', params],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/permissions', {
        params: { query: params },
      })
      if (response.error) throw response.error
      return response.data
    },
  })
}

export const usePermission = (permissionId: string) => {
  return useQuery({
    queryKey: ['permission', permissionId],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/permissions', {
        params: { query: { roleId: permissionId } },
      })
      if (response.error) throw response.error
      return response.data
    },
    enabled: !!permissionId,
  })
}

export const usePermissionTest = () => {
  return useMutation({
    mutationFn: async (testData: PermissionTestRequest) => {
      const response = await identityServiceClient.POST('/permissions/test', {
        body: testData,
      })
      if (response.error) throw response.error
      return response.data
    },
  })
}

// Mutation hooks for permissions
export const useCreatePermission = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (permissionData: any) => {
      const response = await identityServiceClient.POST('/permissions', {
        body: permissionData,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] })
    },
  })
}

export const useUpdatePermission = (permissionId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (permissionData: any) => {
      const response = await identityServiceClient.POST('/permissions', {
        body: { ...permissionData, id: permissionId },
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] })
      queryClient.invalidateQueries({ queryKey: ['permission', permissionId] })
    },
  })
}
